import { Plus } from "lucide-react"
import React, { useState } from "react"
import { Button, Input } from "../../../components/ui"
import api from "../../../utils/axios"
import isLinkValid from "../functions"
import { Pair } from "../types"

/** Resposta de /device-link/create (contrato Story 3.2). */
interface CreateResponse {
    id: number
    code: string
    senha: string
    link: string
    status: Pair["status"]
    createdat: string | null
}

/**
 * Formulário "código lido na TV + link da playlist" → cria um par senha→link.
 * Vive dentro do DeviceModal (modo criar); ao concluir entrega o par criado
 * para o pai trocar para o modo inspecionar sem sair da listagem.
 */
const AddDeviceForm: React.FC<{
    onCreated: (code: string, pair: Pair) => void
    /** Pré-preenche o código (ex.: "adicionar outro acesso" a partir de um par). */
    initialCode?: string
}> = ({ onCreated, initialCode }) => {
    const [codeInput, setCodeInput] = useState(initialCode ?? "")
    const [linkInput, setLinkInput] = useState("")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function handleSubmit(e?: React.FormEvent) {
        e?.preventDefault()
        const code = codeInput.trim().toUpperCase()
        const link = linkInput.trim()

        if (!code) {
            setError("Informe o código que o cliente leu na TV.")
            return
        }
        if (!isLinkValid(link)) {
            setError("Informe um link de playlist válido.")
            return
        }

        const normalizedLink =
            !link.startsWith("http://") && !link.startsWith("https://")
                ? "http://" + link
                : link

        setLoading(true)
        setError(null)
        try {
            const res = await api.post<CreateResponse>("/device-link/create", {
                code,
                link: normalizedLink,
            })
            const d = res.data
            onCreated(d.code ?? code, {
                id: d.id,
                senha: d.senha,
                link: d.link ?? normalizedLink,
                status: d.status ?? "PROVISIONED",
                days_remaining: null,
                validated: null,
                createdat: d.createdat ?? new Date().toISOString(),
            })
        } catch (err: any) {
            const status = err.response?.status
            if (status === 404) {
                setError(
                    "Código não registrado por nenhum dispositivo ainda. Peça ao cliente para abrir o app primeiro e ler o código na TV."
                )
            } else if (status === 403) {
                setError(
                    "Este código pertence a outra conta de revendedor e não pode ser adicionado aqui."
                )
            } else {
                setError(
                    err.response?.data?.error ||
                        "Falha ao adicionar dispositivo. Tente novamente."
                )
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row">
                <div className="flex flex-1 flex-col gap-1">
                    <label
                        className="text-xs font-medium text-muted"
                        htmlFor="device-code"
                    >
                        Código do dispositivo
                    </label>
                    <Input
                        id="device-code"
                        type="text"
                        value={codeInput}
                        onChange={(e) =>
                            setCodeInput(e.target.value.toUpperCase())
                        }
                        autoComplete="off"
                        autoFocus={!initialCode}
                        placeholder="CÓDIGO DA TV..."
                        className="font-mono uppercase tracking-wider"
                    />
                </div>
                <div className="flex flex-[2] flex-col gap-1">
                    <label
                        className="text-xs font-medium text-muted"
                        htmlFor="device-link"
                    >
                        Link da playlist
                    </label>
                    <Input
                        id="device-link"
                        type="text"
                        value={linkInput}
                        onChange={(e) => setLinkInput(e.target.value)}
                        autoComplete="off"
                        autoFocus={!!initialCode}
                        placeholder="https://example.com/playlist"
                        className="font-mono text-xs"
                        invalid={
                            linkInput.length > 0 && !isLinkValid(linkInput)
                        }
                    />
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-danger/40 bg-danger-soft p-3">
                    <p className="text-sm text-danger-text">{error}</p>
                </div>
            )}

            <Button
                type="submit"
                loading={loading}
                leftIcon={loading ? undefined : <Plus size={16} />}
                className="w-full sm:w-auto sm:self-end"
            >
                {loading ? "Adicionando..." : "Adicionar"}
            </Button>
        </form>
    )
}

export default AddDeviceForm
